import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createCrmPhoneActivity, searchCrmAll, searchCrmContacts } from '../api/dynamics365api';
import { useSelector } from '../hook-utils';
import { CallState, CallType, CrmRecord, CrmSettings, PopContactBehaviour, PopContactEvent } from '../model';
import { actions } from '../store';
import { selectMyAccess } from '../store/reducers/config';
import { selectCrmSettings } from '../store/reducers/crmIntegration';
import { selectMyActiveCall } from '../store/reducers/media';
import CrmContactSelectionView from './CrmContactSelectionView';
import css from './CrmIntegrationView.module.scss';
import Toggle from './Toggle';

export function openWindowCrmLink(url: string) {
  window.open(url, 'ipfx-window-crm-user-profile')
}

function openWindowCrmPhoneActivityLink(url: string) {
  window.open(url, 'ipfx-window-crm-phone-activity' + Math.random())
}

export default function CrmIntegrationView() {
  const call = useSelector(selectMyActiveCall)
  const access = useSelector(selectMyAccess)
  let crmSettings = useSelector(selectCrmSettings)
  const dispatch = useDispatch()
  const records = useSelector(s => s.crmIntegration.contacts)
  const [searchComplete, setSearchComplete] = useState(false)
  const enableOverrideSettings = false  // for testing during when I'm writing code, never true in release.
  const [override, setOverride] = useState(false)
  const [overrideSettings, setOverrideSettings] = useState<CrmSettings>({
    defaultCrmPage: '',
    defaultNoContactResultPage: '',
    handleCallBacks: true,
    handleDirectCalls: true,
    integrationEnabled: true,
    popContactBehaviour: 'Auto',
    popContactEvent: "Ring",
    popOutgoing: true,
    popPhoneCall: true,
  })

  if (override) {
    crmSettings = { ...overrideSettings, defaultCrmPage: crmSettings.defaultCrmPage, defaultNoContactResultPage: crmSettings.defaultNoContactResultPage }
  }

  const renderPortal = access.features.crmIntegration && call && records != null && (records.length > 1 || records.length === 1 && crmSettings.popContactBehaviour === 'ContactSelection')

  const clid = call && call.clid
  const isUnknownCaller = call != null && call.clid === ''
  const needSearch = crmSettings.integrationEnabled && call !== null && (clid || isUnknownCaller)
    && (records === null || records.length === 0)
    && !searchComplete
    && (crmSettings.popOutgoing && call.callType === CallType.Outgoing && call.status === CallState.OutboundRinging
      // incoming queue call, normal queue calls
      || crmSettings.popContactEvent !== 'None' && call.callType === CallType.Queue && call.status === CallState.ExtensionPending
      // queue call back, caller left a vm on the queue for callback
      || crmSettings.handleCallBacks && call.callType === CallType.Callback && call.status === CallState.OutboundRinging // need to check media.status
      // incoming non-queue call, ie someone ring the agent's ddi.
      || crmSettings.handleDirectCalls && call.callType === CallType.Incoming && call.status === CallState.ExtensionPending)

  function handleContactsLink(crmSettings: CrmSettings, contacts: CrmRecord[]) {
    if (contacts.length === 0) {
      // no results
      openWindowCrmLink(crmSettings.defaultNoContactResultPage)
    } else if (contacts.length === 1) {
      // unique result
      openWindowCrmLink(contacts[0].profileUrl)
    }
  }

  // fetch contact and pop contact if necessary
  useEffect(() => {
    if (!crmSettings.integrationEnabled) return
    if (!access.features.crmIntegration) return
    if (!clid && !isUnknownCaller) return

    let active = true

    async function fetchCrmContacts() {
      let contacts: CrmRecord[] = []
      if (isUnknownCaller) {
        // do nothing
      } else if (clid) {
        contacts = await searchCrmAll(clid)
      }
      if (!active) return

      setSearchComplete(true)
      dispatch(actions.setCrmContacts(contacts))
      if (crmSettings.popContactEvent === 'Ring'
        && (crmSettings.popContactBehaviour === 'Auto' || crmSettings.popContactBehaviour === 'ContactSelection' && contacts.length === 0)) {
        // auto or when there's no point in popping up contact selection because there's no contacts to list.
        handleContactsLink(crmSettings, contacts)
      }
    }

    if (needSearch) {
      fetchCrmContacts()
      return () => { active = false }
    }
  }, [access.features.crmIntegration, clid, crmSettings, dispatch, isUnknownCaller, needSearch])

  const createPhoneActivity = useCallback(async (crmRecord: CrmRecord | null) => {
    if (call === null) return

    const phoneRecord = await createCrmPhoneActivity({
      accountId: crmRecord?.type === 'Account' ? crmRecord.accountId : '',
      contactId: crmRecord?.type === 'Contact' ? crmRecord.contactId : '',
      phoneNumber: call.clid,
      isIncoming: call.callType === CallType.Incoming || call.callType === CallType.Queue,
    })
    if (phoneRecord.id && phoneRecord.activityUrl) {
      openWindowCrmPhoneActivityLink(phoneRecord.activityUrl)
    }
  }, [call])

  // create and open the phone call activity if the contact result is unique when it is answered
  const isExtensionAnswered = call !== null && call.status === CallState.ExtensionAnswered
  useEffect(() => {
    if (!crmSettings.integrationEnabled) return
    if (!access.features.crmIntegration) return
    if (records === null) return

    if (isExtensionAnswered
      && (crmSettings.popContactBehaviour === 'Auto' || crmSettings.popContactBehaviour === 'ContactSelection' && records.length === 0)) {
      // auto or when there's no point in popping up contact selection because there's no contacts to list.
      if (crmSettings.popContactEvent === 'Answer') {
        handleContactsLink(crmSettings, records)
      }

      if (crmSettings.popPhoneCall) {
        createPhoneActivity(records.length === 0 ? null : records[0])
      }
      dispatch(actions.setCrmContacts(null))
    }
  }, [access.features.crmIntegration, createPhoneActivity, crmSettings, dispatch, isExtensionAnswered, records])

  // clear the contacts whenever there's no more calls.
  useEffect(() => {
    if (!crmSettings.integrationEnabled) return
    if (call === null) {
      setSearchComplete(false)
      dispatch(actions.setCrmContacts(null))
    }
  }, [call, crmSettings.integrationEnabled, dispatch])

  function renderOverrideSettings() {
    return <div className={css.settingsOverrideContainer}>
      Test Vaious CRM Settings
      <Toggle value={override} onChange={c => { setOverride(c) }} >Override Settings</Toggle>
      <Toggle value={overrideSettings.handleCallBacks} onChange={c => { setOverrideSettings({ ...overrideSettings, handleCallBacks: c }) }} >Pop Callbacks</Toggle>
      <Toggle value={overrideSettings.handleDirectCalls} onChange={c => { setOverrideSettings({ ...overrideSettings, handleDirectCalls: c }) }} >Pop Direct Calls</Toggle>
      <Toggle value={overrideSettings.popOutgoing} onChange={c => { setOverrideSettings({ ...overrideSettings, popOutgoing: c }) }} >Pop Outgoing</Toggle>
      <Toggle value={overrideSettings.popPhoneCall} onChange={c => { setOverrideSettings({ ...overrideSettings, popPhoneCall: c }) }} >Pop Phone Call Activity</Toggle>
      <label>Pop Contact Behaviour</label>
      <select value={overrideSettings.popContactBehaviour} onChange={e => {
        setOverrideSettings({ ...overrideSettings, popContactBehaviour: e.currentTarget.value as PopContactBehaviour })
      }}>
        <option key='Auto' value='Auto'>Auto</option>
        <option key='ContactSelection' value='ContactSelection'>Contact Selection</option>
      </select>
      <label>Pop Contact Event</label>
      <select value={overrideSettings.popContactEvent} onChange={e => {
        setOverrideSettings({ ...overrideSettings, popContactEvent: e.currentTarget.value as PopContactEvent })
      }}>
        <option key='Ring' value='Ring'>Ring</option>
        <option key='Answer' value='Answer'>Answer</option>
        <option key='None' value='None'>None</option>
      </select>
    </div>
  }

  return <>
    {enableOverrideSettings && renderOverrideSettings()}
    {renderPortal && records != null && <CrmContactSelectionView crmSettings={crmSettings} records={records} createPhoneActivity={createPhoneActivity} />}
  </>
}
